import {
  datadogRum,
  RumEvent,
  RumEventDomainContext,
} from '@datadog/browser-rum';
import { findEnvVar } from 'lib/findEnvVar';
import { datadogLogs } from '@datadog/browser-logs';
import { wasCaughtByBoundary } from 'components/ErrorBoundary/caughtErrors';

const DD_RUM_CLIENT_TOKEN = findEnvVar('DD_RUM_CLIENT_TOKEN');
const DD_RUM_APPLICATION_ID = findEnvVar('DD_RUM_APPLICATION_ID');
const DD_VERSION = findEnvVar('DD_VERSION');
const ENV = findEnvVar('ENV');

// Datadog backend services tag env as 'prod'; the React build identifies
// itself as 'production'. Map at the SDK boundary so cross-service formula
// monitors and dashboards work without an env-naming mismatch. Leave non-prod
// values untouched (staging / sandbox / development already match).
const DD_ENV = ENV === 'production' ? 'prod' : ENV;

const sanitizeUrlToken = (url: string) =>
  url.replace(/token=[^&]*/g, 'token=REDACTED');
// initialize datadog RUM for production environments
// (Assumes token and app ID are only defined for the appropriate environments)
const initializeDataDog = () => {
  if (DD_RUM_CLIENT_TOKEN && DD_RUM_APPLICATION_ID) {
    datadogRum.init({
      clientToken: DD_RUM_CLIENT_TOKEN,
      applicationId: DD_RUM_APPLICATION_ID,
      service: 'candidate-portal',
      env: DD_ENV,
      sessionSampleRate: 15,
      sessionReplaySampleRate: 100,
      // Candidate report content must stay masked in replays; the SDK's own
      // default is the less strict 'mask-user-input'.
      defaultPrivacyLevel: 'mask',
      enablePrivacyForActionName: false,
      trackUserInteractions: true,
      trackResources: false,
      trackLongTasks: false,
      version: DD_VERSION,
      beforeSend(event: RumEvent, context?: RumEventDomainContext) {
        // CSP violations are almost entirely third-party scripts and browser
        // extensions (Google Translate, ShopBack, ad pixels) we don't control,
        // so drop them rather than flood Error Tracking.
        if (
          event.type === 'error' &&
          event.error.source === 'report' &&
          event.error.message.startsWith('csp_violation')
        ) {
          return false;
        }
        // React logs every error a boundary catches to console.error too; the
        // boundary has already reported it, so drop the console copy rather
        // than count each crash twice (ECDEX-3989).
        if (
          event.type === 'error' &&
          event.error.source === 'console' &&
          context &&
          'error' in context &&
          wasCaughtByBoundary(context.error)
        ) {
          return false;
        }
        // Sanitize token from dashboard admin in the url
        event.view.url = sanitizeUrlToken(event.view.url);
        if (event.type === 'resource' && event.resource.url)
          event.resource.url = sanitizeUrlToken(event.resource.url);
        return true;
      },
    });
    datadogRum.startSessionReplayRecording();
    // for RUM, try to mark page views which render while inactive / not visible / hidden
    window.addEventListener('load', () => {
      datadogRum.setGlobalContextProperty('view', {
        // old/MS browsers may not support document.hidden.  If it's not supported, then we don't know
        // for sure the tab was or wasn't hidden, so consider it to have been hidden.
        window_hidden: document.hidden === undefined || document.hidden,
      });
    });

    datadogLogs.init({
      clientToken: DD_RUM_CLIENT_TOKEN,
      site: 'datadoghq.com',
      service: 'candidate-portal',
      env: DD_ENV,
      version: DD_VERSION,
      forwardErrorsToLogs: false,
    });
  }
};

export default initializeDataDog;
